import { guards } from '@shared/helpers/typeguards.helpers';
import { Auth0RopgTokens } from '@shared/models/auth0-ropg.model';

export type ParseAuth0RopgTokenResult =
  { ok: true; tokens: Auth0RopgTokens } | { ok: false; reason: string };

/**
 * Narrow Auth0 `/oauth/token` JSON. Rejects missing access or id tokens.
 */
export const parseAuth0RopgTokenResponse = (
  value: unknown,
): ParseAuth0RopgTokenResult => {
  if (!guards.is.object(value)) {
    return { ok: false, reason: 'Auth0 token response was not an object' };
  }

  const accessToken = value.access_token;
  const idToken = value.id_token;
  const expiresIn = value.expires_in;
  const tokenType = value.token_type;
  if (!guards.is.string(accessToken) || !accessToken) {
    return { ok: false, reason: 'Auth0 token response missing access_token' };
  }
  if (!guards.is.string(idToken) || !idToken) {
    return { ok: false, reason: 'Auth0 token response missing id_token' };
  }
  if (!guards.is.number(expiresIn) || expiresIn <= 0) {
    return { ok: false, reason: 'Auth0 token response missing expires_in' };
  }
  if (!guards.is.string(tokenType) || !tokenType) {
    return { ok: false, reason: 'Auth0 token response missing token_type' };
  }

  const refreshToken = value.refresh_token;
  if (
    guards.is.defined(refreshToken) &&
    (!guards.is.string(refreshToken) || !refreshToken)
  ) {
    return {
      ok: false,
      reason: 'Auth0 token response had an empty refresh_token',
    };
  }

  const scope = value.scope;
  if (guards.is.defined(scope) && !guards.is.string(scope)) {
    return { ok: false, reason: 'Auth0 token response had a non-string scope' };
  }

  return {
    ok: true,
    tokens: {
      access_token: accessToken,
      id_token: idToken,
      expires_in: expiresIn,
      token_type: tokenType,
      ...(guards.is.string(refreshToken)
        ? { refresh_token: refreshToken }
        : {}),
      ...(guards.is.string(scope) ? { scope } : {}),
    },
  };
};

/**
 * Optional refresh_token on the localhost ROPG request body. Other fields are ignored.
 */
export const parseAuth0RopgRequestBody = (
  value: unknown,
): { refreshToken?: string } => {
  if (!guards.is.object(value)) return {};
  const refreshToken = value.refresh_token;
  if (!guards.is.string(refreshToken) || !refreshToken.trim()) return {};
  return { refreshToken: refreshToken.trim() };
};

/**
 * Auth0 `/oauth/token` error payload. Omits tokens and credentials.
 */
export const formatAuth0TokenError = (value: unknown): string => {
  if (!guards.is.object(value)) {
    return 'Auth0 Resource Owner Password Grant failed';
  }
  const code = guards.is.string(value.error) ? value.error : undefined;
  const description = guards.is.string(value.error_description)
    ? value.error_description
    : undefined;
  if (code && description) return `${code}: ${description}`;
  if (description) return description;
  if (code) return code;
  return 'Auth0 Resource Owner Password Grant failed';
};
